import { useState, useEffect, useRef } from 'react';
import { crmAPI, integrationsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import {
  Plus, X, Save, Trash2, ChevronRight, TrendingUp, DollarSign,
  Trophy, Target, Phone, Mail, Building2, Tag, Calendar,
  MessageSquare, Search, Filter, Edit3, ArrowRight, Briefcase,
  Clock, BarChart2, List, LayoutGrid, User,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STAGES = [
  { id: 'new', label: 'New', color: '#6366f1' },
  { id: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { id: 'qualified', label: 'Qualified', color: '#3b82f6' },
  { id: 'proposal', label: 'Proposal', color: '#8b5cf6' },
  { id: 'negotiation', label: 'Negotiation', color: '#ec4899' },
  { id: 'won', label: 'Won', color: '#22c55e' },
  { id: 'lost', label: 'Lost', color: '#ef4444' },
];

const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.id, s]));

const ACTIVITY_ICONS = {
  deal_created: '✨', stage_change: '→', note_added: '📝',
  call: '📞', email: '✉️', meeting: '🤝', whatsapp: '💬', ai_message: '🤖',
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'MYR', 'INR', 'SGD', 'AED', 'SAR', 'AUD'];

function fmtMoney(val, currency = 'USD') {
  if (!val) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(val);
}

function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(date).toLocaleDateString();
}

// ─── TABS ───────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'pipeline', label: 'Pipeline', icon: LayoutGrid },
  { id: 'deals', label: 'All Deals', icon: List },
  { id: 'stats', label: 'Stats', icon: BarChart2 },
];

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────────
export default function CRMPage() {
  const { merchant, user } = useAuth();
  const { on, off } = useSocket();
  const [activeTab, setActiveTab] = useState('pipeline');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewDeal, setShowNewDeal] = useState(false);

  const [pipeline, setPipeline] = useState(null);
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [pipeRes, statsRes] = await Promise.all([
        crmAPI.getPipeline(),
        crmAPI.getStats(),
      ]);
      setPipeline(pipeRes.data.data);
      setStats(statsRes.data.data);
    } catch (_) {}
    finally { setLoading(false); }
  };

  const loadDeals = async () => {
    try {
      const { data } = await crmAPI.getDeals({ search, limit: 100 });
      setDeals(data.data.deals || []);
    } catch (_) {}
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (activeTab === 'deals') loadDeals(); }, [activeTab, search]);

  // Real-time: add new deal to pipeline board when received via socket
  useEffect(() => {
    const handleNewDeal = (deal) => {
      toast.success(`New deal: ${deal.title}`, { icon: '✨', duration: 4000 });
      // Insert into pipeline stage column
      setPipeline(prev => {
        if (!prev) return prev;
        return prev.map(col =>
          col.stage === deal.stage
            ? { ...col, deals: [deal, ...col.deals], count: col.count + 1, totalValue: col.totalValue + (deal.value || 0) }
            : col
        );
      });
      // Insert into deals list if open
      setDeals(prev => [deal, ...prev]);
    };
    on('crm:deal:new', handleNewDeal);
    return () => off('crm:deal:new', handleNewDeal);
  }, [on, off]);

  const openDeal = async (dealId) => {
    try {
      const { data } = await crmAPI.getDeal(dealId);
      setSelectedDeal(data.data);
    } catch (_) {}
  };

  const onDealSaved = () => { load(); if (activeTab === 'deals') loadDeals(); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">CRM</h1>
          <p className="page-subtitle">Track deals and manage your sales pipeline</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewDeal(true)}>
          <Plus size={15} /> New Deal
        </button>
      </div>

      <div className="page-body">
        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)' }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', fontSize: '0.85rem',
                fontWeight: 600, border: 'none', background: 'transparent', cursor: 'pointer',
                color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: `2px solid ${isActive ? 'var(--primary)' : 'transparent'}`,
                marginBottom: -2, borderRadius: '6px 6px 0 0',
              }}>
                <Icon size={15} /> {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex-center" style={{ height: 200 }}><div className="spinner" /></div>
        ) : (
          <>
            {activeTab === 'pipeline' && pipeline && (
              <PipelineView pipeline={pipeline} onDealClick={openDeal} onStageChange={async (dealId, stage) => {
                await crmAPI.moveStage(dealId, stage);
                load();
              }} />
            )}
            {activeTab === 'deals' && (
              <DealsListView deals={deals} search={search} setSearch={setSearch} onDealClick={openDeal} onDelete={async (id) => {
                if (!window.confirm('Delete this deal?')) return;
                await crmAPI.deleteDeal(id);
                loadDeals(); load();
                toast.success('Deal deleted');
              }} />
            )}
            {activeTab === 'stats' && stats && <StatsView stats={stats} pipeline={pipeline} />}
          </>
        )}
      </div>

      {/* Deal Detail Panel */}
      {selectedDeal && (
        <DealPanel
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onUpdate={async (updates) => {
            await crmAPI.updateDeal(selectedDeal._id, updates);
            const { data } = await crmAPI.getDeal(selectedDeal._id);
            setSelectedDeal(data.data);
            onDealSaved();
          }}
          onStageChange={async (stage) => {
            await crmAPI.moveStage(selectedDeal._id, stage);
            const { data } = await crmAPI.getDeal(selectedDeal._id);
            setSelectedDeal(data.data);
            onDealSaved();
          }}
          onAddNote={async (text) => {
            await crmAPI.addNote(selectedDeal._id, text);
            const { data } = await crmAPI.getDeal(selectedDeal._id);
            setSelectedDeal(data.data);
          }}
          onAddActivity={async (actData) => {
            await crmAPI.addActivity(selectedDeal._id, actData);
            const { data } = await crmAPI.getDeal(selectedDeal._id);
            setSelectedDeal(data.data);
          }}
          onDelete={async () => {
            if (!window.confirm('Delete this deal?')) return;
            await crmAPI.deleteDeal(selectedDeal._id);
            setSelectedDeal(null);
            onDealSaved();
            toast.success('Deal deleted');
          }}
          userName={user?.name}
        />
      )}

      {/* New Deal Modal */}
      {showNewDeal && (
        <NewDealModal
          merchant={merchant}
          onClose={() => setShowNewDeal(false)}
          onCreated={(deal) => { setShowNewDeal(false); onDealSaved(); toast.success('Deal created'); openDeal(deal._id); }}
        />
      )}
    </div>
  );
}

// ─── PIPELINE KANBAN ─────────────────────────────────────────────────────────────
function PipelineView({ pipeline, onDealClick, onStageChange }) {
  const activeStages = STAGES.filter(s => s.id !== 'lost');

  return (
    <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16 }}>
      {activeStages.map(stage => {
        const col = pipeline[stage.id] || { deals: [], total: 0, count: 0 };
        return (
          <div key={stage.id} style={{ minWidth: 220, maxWidth: 240, flexShrink: 0 }}>
            {/* Column header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, padding: '0 2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: stage.color }} />
                <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{stage.label}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg)', borderRadius: 10, padding: '1px 7px', border: '1px solid var(--border)' }}>{col.count}</span>
              </div>
              {col.total > 0 && (
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                  {fmtMoney(col.total)}
                </span>
              )}
            </div>

            {/* Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {col.deals.map(deal => (
                <DealCard key={deal._id} deal={deal} stageColor={stage.color} onClick={() => onDealClick(deal._id)}
                  onMove={(toStage) => onStageChange(deal._id, toStage)} />
              ))}
              {col.deals.length === 0 && (
                <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.75rem', borderRadius: 10, border: '1.5px dashed var(--border)' }}>
                  No deals
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Lost column compact */}
      {pipeline.lost?.count > 0 && (
        <div style={{ minWidth: 190, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, padding: '0 2px' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444' }} />
            <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>Lost</span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'var(--bg)', borderRadius: 10, padding: '1px 7px', border: '1px solid var(--border)' }}>{pipeline.lost.count}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pipeline.lost.deals.map(deal => (
              <DealCard key={deal._id} deal={deal} stageColor="#ef4444" onClick={() => onDealClick(deal._id)} onMove={null} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DealCard({ deal, stageColor, onClick, onMove }) {
  const [showMove, setShowMove] = useState(false);
  const moveStages = STAGES.filter(s => s.id !== deal.stage);

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--white)', borderRadius: 10, padding: '10px 12px',
        border: '1.5px solid var(--border)', cursor: 'pointer',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)', position: 'relative',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = stageColor; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}
    >
      <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: 4, lineHeight: 1.3 }}>{deal.title}</div>
      {deal.contactName && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>{deal.contactName}</div>}
      {deal.contactCompany && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{deal.contactCompany}</div>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
        {deal.value > 0 ? (
          <span style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--primary)' }}>{fmtMoney(deal.value, deal.currency)}</span>
        ) : <span />}
        <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{timeAgo(deal.createdAt)}</span>
      </div>

      {onMove && (
        <div onClick={e => { e.stopPropagation(); setShowMove(p => !p); }}
          style={{ position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', border: '1px solid var(--border)', cursor: 'pointer' }}>
          <ArrowRight size={10} />
        </div>
      )}

      {showMove && (
        <div onClick={e => e.stopPropagation()}
          style={{ position: 'absolute', top: 30, right: 8, background: 'var(--white)', border: '1.5px solid var(--border)', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 140, padding: '4px 0' }}>
          {moveStages.map(s => (
            <div key={s.id} onClick={() => { onMove(s.id); setShowMove(false); }}
              style={{ padding: '7px 14px', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── DEALS LIST VIEW ─────────────────────────────────────────────────────────────
function DealsListView({ deals, search, setSearch, onDealClick, onDelete }) {
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ position: 'relative', maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" placeholder="Search deals..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32, fontSize: '0.83rem' }} />
        </div>
      </div>

      {deals.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          No deals yet. Create your first deal to get started.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                {['Deal', 'Contact', 'Stage', 'Value', 'Priority', 'Created', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deals.map(deal => {
                const stage = STAGE_MAP[deal.stage];
                return (
                  <tr key={deal._id} onClick={() => onDealClick(deal._id)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 14px', fontWeight: 600, maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.title}</div>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>
                      <div>{deal.contactName || '-'}</div>
                      {deal.contactCompany && <div style={{ fontSize: '0.72rem' }}>{deal.contactCompany}</div>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.75rem', fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: stage?.color + '22', color: stage?.color }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: stage?.color }} />
                        {stage?.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--primary)' }}>
                      {deal.value > 0 ? fmtMoney(deal.value, deal.currency) : '-'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <PriorityBadge priority={deal.priority} />
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {new Date(deal.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-sm" onClick={() => onDelete(deal._id)}><Trash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PriorityBadge({ priority }) {
  const colors = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: (colors[priority] || '#94a3b8') + '22', color: colors[priority] || '#94a3b8', textTransform: 'capitalize' }}>
      {priority || 'medium'}
    </span>
  );
}

// ─── STATS VIEW ──────────────────────────────────────────────────────────────────
function StatsView({ stats, pipeline }) {
  const statCards = [
    { label: 'Pipeline Value', value: fmtMoney(stats.pipelineValue), icon: DollarSign, color: '#6366f1' },
    { label: 'Won Value', value: fmtMoney(stats.wonValue), icon: Trophy, color: '#22c55e' },
    { label: 'Win Rate', value: `${stats.winRate}%`, icon: Target, color: '#f59e0b' },
    { label: 'Avg Deal Size', value: fmtMoney(stats.avgDealSize), icon: TrendingUp, color: '#3b82f6' },
    { label: 'Open Deals', value: stats.open, icon: Briefcase, color: '#8b5cf6' },
    { label: 'New This Month', value: stats.newThisMonth, icon: Plus, color: '#ec4899' },
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        {statCards.map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="card" style={{ padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: card.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={16} style={{ color: card.color }} />
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>{card.label}</div>
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800 }}>{card.value}</div>
            </div>
          );
        })}
      </div>

      {/* Stage breakdown */}
      {pipeline && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 14 }}>Pipeline by Stage</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {STAGES.map(stage => {
              const col = pipeline[stage.id] || { count: 0, total: 0 };
              const maxTotal = Math.max(...STAGES.map(s => pipeline[s.id]?.total || 0), 1);
              return (
                <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 90, fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', flexShrink: 0 }}>{stage.label}</div>
                  <div style={{ flex: 1, height: 8, background: 'var(--bg)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 4, background: stage.color, width: `${(col.total / maxTotal) * 100}%`, minWidth: col.count > 0 ? 4 : 0 }} />
                  </div>
                  <div style={{ width: 50, textAlign: 'right', fontSize: '0.75rem', fontWeight: 700 }}>{col.count}</div>
                  <div style={{ width: 90, textAlign: 'right', fontSize: '0.75rem', color: 'var(--text-muted)' }}>{col.total > 0 ? fmtMoney(col.total) : '-'}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DEAL DETAIL PANEL ───────────────────────────────────────────────────────────
function DealPanel({ deal, onClose, onUpdate, onStageChange, onAddNote, onAddActivity, onDelete, userName }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [noteText, setNoteText] = useState('');
  const [actType, setActType] = useState('call');
  const [actDesc, setActDesc] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [panelTab, setPanelTab] = useState('timeline');

  useEffect(() => {
    setForm({
      title: deal.title,
      contactName: deal.contactName,
      contactEmail: deal.contactEmail,
      contactPhone: deal.contactPhone,
      contactCompany: deal.contactCompany,
      value: deal.value,
      currency: deal.currency || 'USD',
      priority: deal.priority || 'medium',
      expectedCloseDate: deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toISOString().slice(0, 10) : '',
    });
  }, [deal]);

  const saveEdit = async () => {
    await onUpdate({ ...form, value: parseFloat(form.value) || 0 });
    setEditing(false);
    toast.success('Deal updated');
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    await onAddNote(noteText.trim());
    setNoteText('');
    setSavingNote(false);
  };

  const submitActivity = async () => {
    if (!actDesc.trim()) return;
    await onAddActivity({ type: actType, description: actDesc });
    setActDesc('');
    toast.success('Activity logged');
  };

  const stage = STAGE_MAP[deal.stage];
  const sortedActivities = [...(deal.activities || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end',
    }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />

      {/* Panel */}
      <div style={{
        position: 'relative', width: '100%', maxWidth: 520, background: 'var(--white)',
        height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: stage?.color }} />
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: stage?.color }}>{stage?.label}</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p => !p)}><Edit3 size={13} /></button>
              <button className="btn btn-ghost btn-sm" onClick={onDelete}><Trash2 size={13} /></button>
              <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={15} /></button>
            </div>
          </div>

          {editing ? (
            <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8 }} />
          ) : (
            <h2 style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 6 }}>{deal.title}</h2>
          )}

          {/* Stage selector */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {STAGES.map(s => (
              <button key={s.id} onClick={() => onStageChange(s.id)} style={{
                padding: '3px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                background: deal.stage === s.id ? s.color : 'var(--bg)',
                color: deal.stage === s.id ? '#fff' : 'var(--text-muted)',
                transition: 'all 0.15s',
              }}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Edit form / Contact info */}
          {editing ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Contact Name</label><input className="form-input" value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Company</label><input className="form-input" value={form.contactCompany} onChange={e => setForm(f => ({ ...f, contactCompany: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Phone</label><input className="form-input" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Email</label><input className="form-input" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Value</label><input className="form-input" type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Currency</label>
                <select className="form-input" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Priority</label>
                <select className="form-input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Expected Close</label><input className="form-input" type="date" value={form.expectedCloseDate} onChange={e => setForm(f => ({ ...f, expectedCloseDate: e.target.value }))} /></div>
              <div style={{ gridColumn: '1/-1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={saveEdit}><Save size={12} /> Save</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, padding: '12px 14px', background: 'var(--bg)', borderRadius: 10 }}>
              {[
                { icon: User, label: 'Contact', value: deal.contactName || '-' },
                { icon: Building2, label: 'Company', value: deal.contactCompany || '-' },
                { icon: Phone, label: 'Phone', value: deal.contactPhone || '-' },
                { icon: Mail, label: 'Email', value: deal.contactEmail || '-' },
                { icon: DollarSign, label: 'Value', value: deal.value > 0 ? fmtMoney(deal.value, deal.currency) : '-' },
                { icon: Target, label: 'Priority', value: <PriorityBadge priority={deal.priority} /> },
                { icon: Tag, label: 'Source', value: <span style={{ textTransform: 'capitalize', fontSize: '0.78rem' }}>{deal.source || 'manual'}</span> },
                { icon: Calendar, label: 'Close Date', value: deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString() : '-' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} style={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  <Icon size={13} style={{ color: 'var(--text-muted)', marginTop: 3, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Panel tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
            {[{ id: 'timeline', label: 'Timeline' }, { id: 'notes', label: 'Notes' }, { id: 'log', label: 'Log Activity' }].map(t => (
              <button key={t.id} onClick={() => setPanelTab(t.id)} style={{
                padding: '7px 14px', fontSize: '0.8rem', fontWeight: 600, border: 'none', background: 'transparent', cursor: 'pointer',
                color: panelTab === t.id ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: `2px solid ${panelTab === t.id ? 'var(--primary)' : 'transparent'}`,
                marginBottom: -1,
              }}>{t.label}</button>
            ))}
          </div>

          {/* Timeline */}
          {panelTab === 'timeline' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sortedActivities.map((act, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, fontSize: '0.8rem' }}>
                  <div style={{ fontSize: '1rem', flexShrink: 0, marginTop: 1 }}>{ACTIVITY_ICONS[act.type] || '•'}</div>
                  <div style={{ flex: 1 }}>
                    <div>{act.description}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {act.createdByName} · {timeAgo(act.createdAt)}
                    </div>
                  </div>
                </div>
              ))}
              {sortedActivities.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>No activity yet.</div>}
            </div>
          )}

          {/* Notes */}
          {panelTab === 'notes' && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                {(deal.notes || []).slice().reverse().map((note, i) => (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{note.text}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>{note.createdByName} · {timeAgo(note.createdAt)}</div>
                  </div>
                ))}
                {!deal.notes?.length && <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>No notes yet.</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <textarea className="form-input" rows={2} placeholder="Add a note..." value={noteText}
                  onChange={e => setNoteText(e.target.value)} style={{ flex: 1, fontSize: '0.82rem', resize: 'none' }} />
                <button className="btn btn-primary btn-sm" onClick={submitNote} disabled={savingNote || !noteText.trim()}>
                  <Save size={13} />
                </button>
              </div>
            </div>
          )}

          {/* Log Activity */}
          {panelTab === 'log' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Activity Type</label>
                <select className="form-input" value={actType} onChange={e => setActType(e.target.value)}>
                  <option value="call">📞 Call</option>
                  <option value="email">✉️ Email</option>
                  <option value="meeting">🤝 Meeting</option>
                  <option value="whatsapp">💬 WhatsApp</option>
                  <option value="note_added">📝 Note</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={3} placeholder="What happened?" value={actDesc}
                  onChange={e => setActDesc(e.target.value)} />
              </div>
              <button className="btn btn-primary btn-sm" onClick={submitActivity} disabled={!actDesc.trim()}>
                Log Activity
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── NEW DEAL MODAL ───────────────────────────────────────────────────────────────
function NewDealModal({ merchant, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '', contactName: '', contactEmail: '', contactPhone: '', contactCompany: '',
    value: '', currency: merchant?.currency || 'USD', priority: 'medium', stage: 'new', source: 'manual',
    expectedCloseDate: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const { data } = await crmAPI.createDeal({ ...form, value: parseFloat(form.value) || 0 });
      onCreated(data.data);
    } catch (_) {
      toast.error('Failed to create deal');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <span className="modal-title">New Deal</span>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group" style={{ gridColumn: '1/-1', marginBottom: 0 }}>
            <label className="form-label">Deal Title *</label>
            <input className="form-input" placeholder="e.g. Website redesign for ABC Corp" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Contact Name</label>
            <input className="form-input" placeholder="John Smith" value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Company</label>
            <input className="form-input" placeholder="ACME Corp" value={form.contactCompany} onChange={e => setForm(f => ({ ...f, contactCompany: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Phone</label>
            <input className="form-input" placeholder="+1 234 567 8900" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email</label>
            <input className="form-input" placeholder="john@company.com" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Deal Value</label>
            <input className="form-input" type="number" placeholder="0" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Currency</label>
            <select className="form-input" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Stage</label>
            <select className="form-input" value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}>
              {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Priority</label>
            <select className="form-input" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1', marginBottom: 0 }}>
            <label className="form-label">Expected Close Date</label>
            <input className="form-input" type="date" value={form.expectedCloseDate} onChange={e => setForm(f => ({ ...f, expectedCloseDate: e.target.value }))} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Plus size={13} /> {saving ? 'Creating...' : 'Create Deal'}
          </button>
        </div>
      </div>
    </div>
  );
}
